import { useCallback, useEffect, useState, type FC } from "react";

import { ExternalLink } from "@/components/flexkit/typography/ExternalLink";
import { Nudge } from "@/components/nudges/Nudge";
import { NudgeKeys, useNudges } from "@/hooks/use-nudges";
import { TrackLocations } from "@/hooks/use-segment";
import { useSetPreference } from "@/queries/user-preferences-queries";

export const PrivacyPolicyNudge: FC = () => {
    const { active: nudge, error, isPending } = useNudges();
    const { mutate: setPreference } = useSetPreference();
    const [open, setOpen] = useState(false);

    // Auto-show nudge if applicable
    useEffect(() => {
        if (isPending || error) {
            return;
        }

        if (nudge?.key === NudgeKeys.PRIVACY_POLICY_UPDATED) {
            setOpen(true);
            return;
        }
        setOpen(false);
    }, [isPending, error, nudge?.key]);

    const toggleOpen = useCallback(() => {
        setOpen((previousOpenState) => {
            const isNowOpen = !previousOpenState;
            if (!isNowOpen) {
                setPreference({
                    key: NudgeKeys.PRIVACY_POLICY_UPDATED,
                    value: new Date().toISOString(),
                });
            }
            return isNowOpen;
        });
    }, [setPreference]);

    const ariaLabelPrefix = "Announcement - Privacy Policy";
    return (
        <Nudge
            open={open}
            toggleOpen={toggleOpen}
            ariaLabelPrefix={ariaLabelPrefix}
            data-testid="nudge-privacy-policy-trigger"
            data-track-location={TrackLocations.PrivacyPolicyNudge}
        >
            <Nudge.Content>
                <Nudge.Header>
                    <Nudge.Title>Privacy Policy</Nudge.Title>
                    <Nudge.Description>We&apos;ve updated our Privacy Policy.</Nudge.Description>
                </Nudge.Header>
                <Nudge.Action variant="primary" className="px-4" aria-label={`${ariaLabelPrefix} - Learn more`} asChild>
                    <ExternalLink href="https://ona.com/legal/privacy-policy" iconSize="sm">
                        Learn more
                    </ExternalLink>
                </Nudge.Action>
            </Nudge.Content>
        </Nudge>
    );
};
